// Pannello di configurazione integrazione Firebase Cloud Messaging (FCM)
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  tokenExpiryDays: z.coerce.number().int().min(1).max(365).default(90),
  enableIosNotifications: z.boolean().default(true),
  enableAndroidNotifications: z.boolean().default(true),
  androidChannelId: z.string().min(1).max(50).default('fieldcore_default'),
  defaultBadgeCount: z.coerce.number().int().min(0).max(99).default(1),
})

type FormValues = z.infer<typeof schema>

interface FCMConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function FCMConfig({ currentMetadata, onSaved }: FCMConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      tokenExpiryDays: (currentMetadata?.['tokenExpiryDays'] as number) ?? 90,
      enableIosNotifications: (currentMetadata?.['enableIosNotifications'] as boolean) ?? true,
      enableAndroidNotifications: (currentMetadata?.['enableAndroidNotifications'] as boolean) ?? true,
      androidChannelId: (currentMetadata?.['androidChannelId'] as string) ?? 'fieldcore_default',
      defaultBadgeCount: (currentMetadata?.['defaultBadgeCount'] as number) ?? 1,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/fcm/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['FIREBASE_CREDENTIALS_JSON']} />

      <div className="rounded-lg border border-[var(--fc-border)] p-3 bg-[var(--fc-surface)]">
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          <span className="font-medium text-[var(--fc-foreground)]">Configurazione Firebase:</span>{' '}
          Scaricare il file service account JSON dalla Firebase Console (Impostazioni progetto &rarr;
          Account di servizio &rarr; Genera nuova chiave privata) e inserire il contenuto completo
          nella variabile{' '}
          <span className="font-mono text-[var(--fc-primary)]">FIREBASE_CREDENTIALS_JSON</span>.
        </p>
      </div>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Scadenza token device (giorni)
          </label>
          <input
            type="number"
            min={1}
            max={365}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('tokenExpiryDays')}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            I token non utilizzati da questo numero di giorni vengono rimossi automaticamente.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Android Notification Channel ID
          </label>
          <input
            type="text"
            maxLength={50}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)] font-mono"
            {...register('androidChannelId')}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Deve corrispondere all'ID del canale configurato nell'app Android.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Badge count default (iOS)
          </label>
          <input
            type="number"
            min={0}
            max={99}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('defaultBadgeCount')}
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-[var(--fc-foreground)] mb-2">Piattaforme abilitate</legend>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="enableIosNotifications" className="h-4 w-4 rounded border-[var(--fc-border)]" {...register('enableIosNotifications')} />
              <label htmlFor="enableIosNotifications" className="text-sm text-[var(--fc-foreground)]">Notifiche iOS (APNs)</label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="enableAndroidNotifications" className="h-4 w-4 rounded border-[var(--fc-border)]" {...register('enableAndroidNotifications')} />
              <label htmlFor="enableAndroidNotifications" className="text-sm text-[var(--fc-foreground)]">Notifiche Android (FCM)</label>
            </div>
          </div>
        </fieldset>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>Annulla</Button>
          <Button type="submit" isLoading={saveMutation.isPending}>Salva configurazione</Button>
        </div>
      </form>
    </div>
  )
}
